import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { socialLinks } from "@/components/layout/Footer";
import cookbookCover from "@/assets/cookbook-cover.webp";
import ayaLogo from "@/assets/aya-youth-collective.jpg.asset.json";

const purchaseLinks = [
  { label: "Canada - Indie Bookstores", url: "https://bookmanager.com" },
  { label: "Barnes and Noble", url: "https://www.barnesandnoble.com" },
  { label: "Canada - Indigo Books", url: "https://www.chapters.indigo.ca" },
  { label: "Amazon", url: "https://amzn.to/4uNbd9H" },
  { label: "UK - Blackwells", url: "https://blackwells.co.uk" },
  { label: "Bookshop.org", url: "https://bookshop.org" },
  { label: "UK - Amazon", url: "https://amazon.co.uk" },
];

const isBookLink = (t: string) => /book|cookbook/i.test(t);
const isEventLink = (t: string) => /event/i.test(t);
const isBlogLink = (t: string) => /blog/i.test(t);
const isThriveMarket = (t: string) => /thrive/i.test(t);

const SocialLinksPopover = () => {
  const [open, setOpen] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);
  const [links, setLinks] = useState<any[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!open || loaded) return;
    supabase
      .from("linktree_links")
      .select("*")
      .eq("is_active", true)
      .order("display_order")
      .then(({ data }) => {
        setLinks(data ?? []);
        setLoaded(true);
      });
  }, [open, loaded]);

  const grouped: { section: string; items: any[] }[] = [];
  for (const link of links) {
    const sec = link.section || "";
    const last = grouped[grouped.length - 1];
    if (!last || last.section !== sec) grouped.push({ section: sec, items: [link] });
    else last.items.push(link);
  }

  return (
    <>
      <button
        aria-label="Open @applesauceandadhd links menu"
        className="text-primary hover:underline font-body cursor-pointer inline"
        onClick={() => setOpen(true)}
      >
        @applesauceandadhd
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-wood-light max-w-sm bg-wood p-0 gap-0 [&>button]:text-sidebar-foreground [&>button]:opacity-100 [&>button]:hover:text-gold">
          <DialogTitle className="sr-only">Links</DialogTitle>

          {/* Header */}
          <div className="text-center pt-5 px-4">
            <h2 className="font-heading text-lg font-bold text-gold uppercase tracking-widest">Jess Secrest</h2>
            <p className="font-heading text-xs text-muted-foreground tracking-wide mt-0.5">@applesauceandadhd</p>
            <div className="flex justify-center gap-3 mt-2">
              {socialLinks.map((s) => (
                <a key={s.label} href={s.url} target="_blank" rel="noopener noreferrer" aria-label={s.label}
                  className="text-sidebar-foreground hover:text-gold transition-colors">
                  <s.icon className="h-5 w-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Scrollable links */}
          <ScrollArea className="max-h-[55vh] px-4 pb-4 mt-3">
            <div className="space-y-2">
              {/* AYA Youth Collective pair - grouped in one card */}
              <div className="rounded-xl border-2 border-gold/40 bg-wood-dark/40 p-2.5 shadow-lg shadow-black/20">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-11 h-11 rounded-md overflow-hidden flex-shrink-0 border border-wood-light bg-background p-1">
                    <img src={ayaLogo.url} alt="AYA Youth Collective" className="w-full h-full object-contain" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-heading text-[11px] uppercase tracking-wider text-gold font-bold leading-tight">Supporting AYA Youth Collective</h3>
                    <p className="font-body text-[10px] text-muted-foreground leading-snug mt-0.5">Two ways to help.</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <a
                    href="https://ayayouth.org/"
                    target="_blank" rel="noopener noreferrer"
                    className="flex flex-col items-center justify-center rounded-lg px-4 py-2 border border-wood-light hover:border-gold-dark hover:brightness-110 hover:scale-[1.02] transition-all duration-200 w-full bg-wood-light"
                  >
                    <span className="font-heading text-xs uppercase tracking-wide text-sidebar-foreground font-bold">AYA Youth Collective</span>
                    <span className="font-heading text-[9px] uppercase tracking-wide text-gold mt-0.5">Learn about the organization</span>
                  </a>
                  <a
                    href="https://www.amazon.com/hz/wishlist/ls/1WMZAEMKBT1SD/ref=hz_ls_biz_ex"
                    target="_blank" rel="noopener noreferrer"
                    className="flex flex-col items-center justify-center rounded-lg px-4 py-2 border border-wood-light hover:border-gold-dark hover:brightness-110 hover:scale-[1.02] transition-all duration-200 w-full bg-wood-light"
                  >
                    <span className="font-heading text-xs uppercase tracking-wide text-sidebar-foreground font-bold">Amazon Wishlist</span>
                    <span className="font-heading text-[9px] uppercase tracking-wide text-gold mt-0.5">Donate items from their wishlist</span>
                  </a>
                  <a
                    href="https://ayayouth.org/support/"
                    target="_blank" rel="noopener noreferrer"
                    className="flex flex-col items-center justify-center rounded-lg px-4 py-2 border border-wood-light hover:border-gold-dark hover:brightness-110 hover:scale-[1.02] transition-all duration-200 w-full bg-wood-light"
                  >
                    <span className="font-heading text-xs uppercase tracking-wide text-sidebar-foreground font-bold">Support AYA</span>
                    <span className="font-heading text-[9px] uppercase tracking-wide text-gold mt-0.5">Ways to give and get involved</span>
                  </a>
                </div>
              </div>


              {/* Habitat Kent - Raise The Roof */}
              <a
                href="https://habitatkent.givevirtuous.org/Event/raise-the-roof-an-evening-of-community-connection--impact"
                target="_blank" rel="noopener noreferrer"
                className="flex flex-col items-center justify-center rounded-lg px-4 py-2.5 border border-wood-light hover:border-gold-dark hover:brightness-110 hover:scale-[1.02] transition-all duration-200 w-full bg-wood-light"
              >
                <span className="font-heading text-xs uppercase tracking-wide text-sidebar-foreground font-bold">Raise The Roof</span>
                <span className="font-heading text-[9px] uppercase tracking-wide text-gold mt-0.5">An Evening Of Community, Connection &amp; Impact — Habitat Kent</span>
              </a>

              {/* Habitat Kent - Silent Auction */}
              <a
                href="https://www.zeffy.com/en-US/ticketing/raise-the-roof-habitat-kents-silent-auction"
                target="_blank" rel="noopener noreferrer"
                className="flex flex-col items-center justify-center rounded-lg px-4 py-2.5 border border-wood-light hover:border-gold-dark hover:brightness-110 hover:scale-[1.02] transition-all duration-200 w-full bg-wood-light"
              >
                <span className="font-heading text-xs uppercase tracking-wide text-sidebar-foreground font-bold">Raise The Roof Silent Auction</span>
                <span className="font-heading text-[9px] uppercase tracking-wide text-gold mt-0.5">Bid To Support Habitat Kent</span>
              </a>

              {/* O-Cedar */}
              <a
                href="https://www.ocedar.com/punch-up-the-joy-awards"
                target="_blank" rel="noopener noreferrer"
                className="flex flex-col items-center justify-center rounded-lg px-4 py-2.5 border border-wood-light hover:border-gold-dark hover:brightness-110 hover:scale-[1.02] transition-all duration-200 w-full bg-wood-light"
              >
                <span className="font-heading text-xs uppercase tracking-wide text-sidebar-foreground font-bold">O-Cedar Punch Up The Joy Awards</span>
                <span className="font-heading text-[9px] uppercase tracking-wide text-gold mt-0.5">Vote for your everyday joy pick</span>
              </a>


              {/* Walmart Storefront */}
              <a
                href="https://www.walmart.com/creator/storefront?creator=applesauceandadhd&irgwc=1&afsrc=1&veh=aff&wmlspartner_creator=imp_4489415&affiliates_ad_id_creator=1398372&campaign_id_creator=16662&clickid=QE6Wu-2MkxyZUhiUKRwD7SqbUkuwbr3eI3iv0o0&sourceid=imp_QE6Wu-2MkxyZUhiUKRwD7SqbUkuwbr3eI3iv0o0&wmlspartner=imp_3699778&affiliates_ad_id=565706&campaign_id=9383&sharedid=storefront"
                target="_blank" rel="noopener noreferrer"
                className="flex flex-col items-center justify-center rounded-lg px-4 py-2.5 border border-wood-light hover:border-gold-dark hover:brightness-110 hover:scale-[1.02] transition-all duration-200 w-full bg-wood-light"
              >
                <span className="font-heading text-xs uppercase tracking-wide text-sidebar-foreground font-bold">Walmart Storefront</span>
                <span className="font-heading text-[9px] uppercase tracking-wide text-gold mt-0.5">Shop My Favorite Groceries & Products</span>
              </a>

              {/* Dynamic links */}
              {grouped.map((group, gi) => (
                <div key={gi}>
                  {group.section && (
                    <p className="font-heading text-[10px] uppercase tracking-wider text-center text-muted-foreground mt-3 mb-1">{group.section}</p>
                  )}
                  {group.items.map((link) => {
                    if (isThriveMarket(link.title)) return null;

                    if (isBookLink(link.title) && !isEventLink(link.title)) {
                      return (
                        <button key={link.id} onClick={() => setBookOpen(true)}
                          className="block w-full rounded-xl overflow-hidden border-2 border-gold/40 mb-1 hover:border-gold hover:scale-[1.02] transition-all duration-200 text-left shadow-lg shadow-black/20">
                          <div className="flex bg-gradient-to-br from-wood-light to-wood rounded-xl overflow-hidden">
                            <div className="w-[80px] flex-shrink-0 p-2 flex items-center justify-center bg-wood-dark/50">
                              <img src={cookbookCover} alt="Cookbook" className="w-full rounded-sm" style={{ boxShadow: '-4px 4px 12px rgba(0,0,0,0.45)' }} />
                            </div>
                            <div className="flex-1 py-2 pr-3 flex flex-col justify-center gap-0.5 min-w-0">
                              <h3 className="font-heading text-xs uppercase tracking-wide text-sidebar-foreground font-bold leading-tight">Of Course It's Good!</h3>
                              <span className="inline-flex items-center gap-1 border border-gold/50 text-gold font-heading text-[9px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full w-fit">See Where to Buy</span>
                              <p className="font-handwritten text-gold text-[11px]">(Of course its good, I MADE IT!)</p>
                            </div>
                          </div>
                        </button>
                      );
                    }

                    if (isEventLink(link.title)) {
                      return (
                        <Link key={link.id} to="/events" onClick={() => setOpen(false)}
                          className="block rounded-xl overflow-hidden border-2 border-gold hover:border-gold-dark hover:scale-[1.02] transition-all duration-200 mb-1">
                          <div className="bg-gold p-3 text-center">
                            <h3 className="font-heading text-base font-black uppercase tracking-widest text-background mb-0.5">Book Tour</h3>
                            <p className="font-handwritten text-background/90 text-sm">New stops added!</p>
                            <span className="inline-flex items-center gap-1 bg-background text-gold font-heading text-[9px] font-bold uppercase tracking-wide px-4 py-1.5 rounded-full shadow-md mt-1">View All Events</span>
                          </div>
                        </Link>
                      );
                    }

                    if (isBlogLink(link.title)) {
                      return (
                        <a key={link.id} href={link.url} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-3 rounded-lg px-4 py-2.5 border border-wood-light hover:border-gold-dark hover:brightness-110 hover:scale-[1.02] transition-all duration-200 w-full bg-wood-light mb-1">
                          <span className="flex-1 text-center font-heading text-xs uppercase tracking-wide text-sidebar-foreground font-bold">
                            {link.title}
                          </span>
                        </a>
                      );
                    }

                    return (
                      <a key={link.id} href={link.url} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-2 rounded-lg px-3 py-2.5 border border-wood-light hover:border-gold-dark hover:brightness-110 hover:scale-[1.02] transition-all duration-200 w-full bg-wood-light mb-1">
                        {link.image_url && (
                          <div className="w-9 h-9 rounded-md overflow-hidden flex-shrink-0 border border-wood-light">
                            <img src={link.image_url} alt="" className="w-full h-full object-cover" />
                          </div>
                        )}
                        <span className="flex-1 text-center font-heading text-xs uppercase tracking-wide text-sidebar-foreground font-bold">{link.title}</span>
                      </a>
                    );
                  })}
                </div>
              ))}

              {/* View full page */}
              <Link to="/links" onClick={() => setOpen(false)}
                className="block text-center font-heading text-[10px] uppercase tracking-wider text-gold hover:text-gold-dark transition-colors mt-3 mb-1">
                View Full Page →
              </Link>

              <p className="text-center text-muted-foreground font-body text-[9px]">Links may contain affiliate commissions.</p>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Nested cookbook purchase dialog */}
      <Dialog open={bookOpen} onOpenChange={setBookOpen}>
        <DialogContent className="border-wood-light max-w-sm bg-wood">
          <DialogTitle className="font-heading text-lg uppercase tracking-wide text-sidebar-foreground text-center">Buy My Cookbook</DialogTitle>
          <img src={cookbookCover} alt="Of Course It's Good Cookbook" className="w-28 mx-auto rounded-lg shadow-lg mb-2" />
          <div className="grid grid-cols-1 gap-2">
            {purchaseLinks.map((pl) => (
              <a key={pl.label} href={pl.url} target="_blank" rel="noopener noreferrer"
                className="flex items-center justify-between rounded-lg px-4 py-2.5 border border-wood-light hover:border-gold-dark hover:brightness-110 hover:scale-[1.02] transition-all duration-200 text-sidebar-foreground font-heading text-xs uppercase tracking-wide font-bold bg-wood-light">
                {pl.label}
                <ExternalLink className="h-3.5 w-3.5 ml-2 flex-shrink-0 opacity-60" />
              </a>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SocialLinksPopover;
